import { BmMgmFeeCommitment } from "../../../pojo/bm-classes";
import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { BmItemForm, BmMgmFeeCommitmentForm } from "../../../pojo/bm-form";
import { BmCalcType } from "../../../pojo/bm-enums";
import { BmCalcHelperService } from "../../general/bm-calc-helper.service";

/**
 * SERVICE to manage row COMMITMENT logic in Management Fees Group
 * All this staff is Artem's Fantasy => Will help with understanding: https://www.youtube.com/watch?v=dQw4w9WgXcQ
 *
 * 1) If standardValue changes, all subGroupData where calcType != MANUAL => get newStdVal => GENERATED => disabled
 * 2) If calcType = GENERATED => the input is disabled
 * 3) If a user changes QUARTER => that quarter => MANUAL => enabled.
 * 4) If in a given year the "last" quarter is MANUAL => the year becomes MANUAL, else GENERATED.
 *    The year is always disabled (cannot be directly edited).
 * 5) isCatchUpAvailable = true if there's at least one subGroupData.value != standardValue => show catchUp row
 */
export class MgmFeeCommitmentFormService {

  public static createCommitmentForm(commitment: BmMgmFeeCommitment): FormGroup<BmMgmFeeCommitmentForm> {
    const commitmentForm = new FormGroup<BmMgmFeeCommitmentForm>({
      standardValue: new FormControl(commitment.standardValue ?? 0),
      isCatchUpAvailable: new FormControl<boolean>(false),
      items: new FormArray(commitment.items.map((data) => BmCalcHelperService.createBmItemForm(data))),
    });
    BmCalcHelperService.disableItems(commitmentForm.controls.items);
    MgmFeeCommitmentFormService.updateIsCatchUpAvailable(commitmentForm.controls.items, commitmentForm.value.standardValue);

    return commitmentForm;
  }

  /**
   * Set year value based on last quarter value
   *
   * @param itemsFa commitment items array
   */
  public static updateYearsValues(itemsFa: FormArray<FormGroup<BmItemForm>>): void {
    const itemsByYear = BmCalcHelperService.groupItemsByYear(itemsFa);

    itemsByYear.forEach(({ yearFG, quartersFG }) => {
      const lastQuarter = quartersFG.find((quarter) => quarter.getRawValue().period.quarterNumber === 4);
      const isLastQuarterManual = lastQuarter.getRawValue().calcType === BmCalcType.MANUAL;
      const lastQuarterValue = lastQuarter.getRawValue().itemValue;
      BmCalcHelperService.patchAndDisable(yearFG, lastQuarterValue, isLastQuarterManual ? BmCalcType.CALCULATED : BmCalcType.GENERATED);
    });
  }

  /**
   * Set new standard value in all not Manual items
   *
   * @param itemsFa items FormArray
   * @param newStandardValue new standard value
   */
  public static updateItemsOnStandardValueChange(itemsFa: FormArray<FormGroup<BmItemForm>>, newStandardValue: number): void {
    itemsFa.controls.forEach((item): void => {
      if (item.controls.calcType.value === BmCalcType.GENERATED) {
        BmCalcHelperService.patchAndDisable(item, newStandardValue, BmCalcType.GENERATED);
      }
    });
  }

  /**
   * If at least one commitment Value != to standard value => show Catch Up row (isCatchUpAvailable = true)
   */
  public static updateIsCatchUpAvailable(itemsFa:FormArray<FormGroup<BmItemForm>>, standardValue: number): void {
    const commitmentForm = itemsFa.parent as FormGroup<BmMgmFeeCommitmentForm>;

    for (const item of itemsFa.controls) {
      if (item.getRawValue().itemValue !== standardValue) {
        commitmentForm.controls.isCatchUpAvailable.patchValue(true);
        return; // Exit immediately after first mismatch
      }
    }

    // If we get here, all values match standard
    commitmentForm.controls.isCatchUpAvailable.patchValue(false);
  }
}
